import SearchIcon from '@mui/icons-material/Search';
import { Checkbox, Grid, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { GridCellParams, GridColDef, GridPaginationModel, GridRowsProp } from '@mui/x-data-grid';
import { useConfig, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import FormsService from '../../../../../services/forms-service/forms-service';
import { PacketForms } from '../../../../../services/forms-service/forms-service-types';
import DataGrid from '../../../../shared/data-grid';
import { FilterBar } from './style';

type PacketTabProps = {
    visible: boolean;
    locationId?: number | null;
    selectedPackets: PacketForms[];
    setSelectedPackets: Dispatch<SetStateAction<PacketForms[]>>;
};

const useCallDebounced = (callback: () => void, delay: number) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            callback();
        }, delay);

        return () => {
            clearTimeout(timer);
        };
    }, [callback, delay]);
};

export default function PacketsTab({ visible, locationId, selectedPackets, setSelectedPackets }: PacketTabProps) {
    const [data, setData] = useState([] as GridRowsProp);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);

    const [searchTerm, setSearchTerm] = useState('');

    const { config } = useConfig();
    const oidc = useOidcWithAdmin();
    const formsService = useMemo(() => FormsService.getInstance(config, oidc.oidcService), [config, oidc.oidcService]);

    const callForm = useCallback(async () => {
        try {
            setLoading(true);
            const response = await formsService.listPackets({
                search: searchTerm,
                locationId: locationId ? locationId : undefined,
                page: page
            });
            setData(response.packets);
            setCount(response.count);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    }, [locationId, page, searchTerm, formsService]);

    useCallDebounced(callForm, 500);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchTerm(value);
    };

    const customPacketName = (params: GridCellParams) => (
        <Tooltip title={params.row.formNames} placement='top' arrow>
            <span>{params.value as string}</span>
        </Tooltip>
    );

    const packetsToggleCheckedAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedPackets(() => {
            if (event.target.checked) {
                return data.map((form, index) => ({ formId: form.id, order: index }));
            } else {
                return [];
            }
        });
    };

    const checkedAll = useMemo(() => {
        const selectedPacketsIds = selectedPackets.map(packet => packet.formId);
        const dataIds = data.map(form => form.id);
        return selectedPacketsIds.length === dataIds.length && selectedPacketsIds.every(id => dataIds.includes(id));
    }, [selectedPackets, data]);

    const packetCheckboxHeader = () => <Checkbox onChange={packetsToggleCheckedAll} checked={checkedAll} />;

    const formToggleCheckBox = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedPackets(oldForms => {
            const newForms = oldForms.slice();
            if (event.target.checked) {
                newForms.push({ formId: event.target.value, order: oldForms.length });
            } else {
                const index = oldForms.findIndex(form => form.formId === event.target.value);
                newForms.splice(index, 1);
            }
            return newForms;
        });
    };

    const packetCheckboxCell = (params: GridCellParams) => (
        <div>
            <Checkbox
                checked={!!selectedPackets.find(a => a.formId === params.id) || checkedAll}
                value={params.id}
                onChange={formToggleCheckBox}
            />
        </div>
    );

    const handlePageChange = ({ page }: GridPaginationModel) => {
        setPage(page);
    };

    const packetColumns: GridColDef[] = [
        {
            field: 'col1',
            renderHeader: packetCheckboxHeader,
            flex: 0.2,
            sortable: false,
            filterable: false,
            renderCell: packetCheckboxCell
        },
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: customPacketName
        },
        { field: 'formCount', headerName: 'Forms Included', flex: 1, sortable: false, filterable: false }
    ];

    if (!visible) {
        return null;
    }

    return (
        <>
            <FilterBar container>
                <Grid item xs={12} sm={6}>
                    <TextField
                        onChange={handleSearchChange}
                        error={searchTerm != '' && searchTerm.length < 3}
                        helperText={
                            searchTerm != '' && searchTerm.length < 3 ? 'Search term must be at least 3 characters' : ''
                        }
                        value={searchTerm}
                        label='Search'
                        variant='outlined'
                        id='formSearch'
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <IconButton>
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Grid>
            </FilterBar>
            <DataGrid
                rows={data}
                columns={packetColumns}
                pageSizeOptions={[10]}
                paginationModel={{
                    page,
                    pageSize: 10
                }}
                pagination
                rowCount={count}
                paginationMode='server'
                onPaginationModelChange={handlePageChange}
                autoHeight={true}
                hideMargin
                loading={loading}
                hideFooterSelectedRowCount
                rowHeight={30}
            />
        </>
    );
}
